import React, { FC, useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAppDispatch } from '../redux/store';
import { getBillingAdminGrants } from '../redux/slices/organisation';
import { useAuthenticatedUser } from '../hooks/useAuthenticatedUser';
import { hasFeatureGrant } from '../constants/partnerGrants';
import { RoleName, UserGroup } from '../types/user';
import Loading from '../components/layout/Loading';

/**
 * Route guard for an organisation page that the sidebar only offers to the organisation's
 * admins, and only when the organisation holds a feature grant. Hiding the menu entry removes
 * the entry point; without this, any member could still reach the page by typing the URL
 * (PTEC-1117). The rule is the sidebar's: `allowedRoles: [organisationAdmin]` plus
 * `hasFeatureGrant(features, feature)`, with App Admins admitted on their group as the API is.
 *
 * Grants are fetched here and the decision rests on this guard's own response, never on the
 * shared `billingAdminGrants` slot: the sidebar fills that slot for other organisations, and
 * a late response for one of them must neither deny nor admit here. A response that arrives
 * after the URL has moved to another organisation is ignored. A grants read that fails is
 * treated as no grant.
 *
 * Compose it inside withProtected, as the organisation routes are, e.g.
 * withOrganisationAdminFeature(Page, CONSTITUENTS_FEATURE).
 */
export const withOrganisationAdminFeature = (C: FC, feature: string) => {
	const OrganisationAdminFeatureGuard = (props: any) => {
		const { organisationId = '' } = useParams();
		const dispatch = useAppDispatch();
		const { groups, getOrganisationRoles, loading: userLoading } = useAuthenticatedUser();
		// The feature list read for `organisationId`, or null while that read is outstanding.
		const [decision, setDecision] = useState<{ organisationId: string; features: string[] } | null>(null);

		useEffect(() => {
			if (!organisationId) return;
			let current = true;
			setDecision(null);
			dispatch(getBillingAdminGrants({ organisationId }))
				.unwrap()
				.then((response) => response?.Items?.[0]?.features ?? [])
				.catch(() => [])
				.then((features: string[]) => {
					if (current) setDecision({ organisationId, features });
				});
			return () => {
				current = false;
			};
		}, [dispatch, organisationId]);

		// Wait for the caller and this organisation's grants before deciding, so a refused page
		// never flashes its content and a permitted one never wrongly redirects on first paint.
		if (userLoading || decision?.organisationId !== organisationId) {
			return <Loading />;
		}

		const isAppAdmin = groups.includes(UserGroup.Admin);
		const isOrgAdmin = getOrganisationRoles(organisationId).includes(RoleName.OrgnaisationAdmin);
		if (!(isAppAdmin || isOrgAdmin) || !hasFeatureGrant(decision.features, feature)) {
			return <Navigate to="/404" replace />;
		}

		return <C {...props} />;
	};
	return OrganisationAdminFeatureGuard;
};
